/**
 * Payment Management Types (Level 3.2 & 3.3)
 *
 * Tracks payments both ways:
 * - CustomerPayment: Payments we receive (A/R - Accounts Receivable)
 * - SupplierPayment: Payments we make (A/P - Accounts Payable)
 */
// === HELPER LABELS ===
export const CUSTOMER_PAYMENT_METHOD_LABELS = {
    wire_transfer: 'Wire Transfer',
    check: 'Check',
    credit_card: 'Credit Card',
    ach: 'ACH Transfer'
};
export const CUSTOMER_PAYMENT_STATUS_LABELS = {
    pending: 'Pending',
    cleared: 'Cleared',
    bounced: 'Bounced',
    refunded: 'Refunded'
};
export const SUPPLIER_PAYMENT_METHOD_LABELS = {
    wire_transfer: 'Wire Transfer',
    check: 'Check',
    ach: 'ACH Transfer'
};
export const SUPPLIER_PAYMENT_STATUS_LABELS = {
    pending: 'Pending',
    sent: 'Sent',
    cleared: 'Cleared'
};
export const SUPPLIER_PAYMENT_FOR_LABELS = {
    purchase: 'Purchase',
    memo: 'Memo Conversion',
    other: 'Other'
};
// === COLOR CODING ===
export const CUSTOMER_PAYMENT_STATUS_COLORS = {
    pending: '#f59e0b', // Amber
    cleared: '#10b981', // Green
    bounced: '#ef4444', // Red
    refunded: '#6b7280' // Gray
};
export const SUPPLIER_PAYMENT_STATUS_COLORS = {
    pending: '#f59e0b', // Amber
    sent: '#3b82f6', // Blue
    cleared: '#10b981' // Green
};
/**
 * Validates customer payment data
 */
export function validateCustomerPayment(payment) {
    const errors = [];
    if (!payment.customerId?.trim()) {
        errors.push({ field: 'customerId', message: 'Customer is required' });
    }
    if (!payment.invoiceId?.trim()) {
        errors.push({ field: 'invoiceId', message: 'Invoice is required' });
    }
    if (!payment.paymentDate) {
        errors.push({ field: 'paymentDate', message: 'Payment date is required' });
    }
    if (payment.amount === undefined || payment.amount <= 0) {
        errors.push({ field: 'amount', message: 'Amount must be greater than 0' });
    }
    if (!payment.paymentMethod) {
        errors.push({ field: 'paymentMethod', message: 'Payment method is required' });
    }
    if (!payment.paymentReference?.trim()) {
        errors.push({ field: 'paymentReference', message: 'Payment reference is required' });
    }
    return {
        isValid: errors.length === 0,
        errors
    };
}
/**
 * Validates supplier payment data
 */
export function validateSupplierPayment(payment) {
    const errors = [];
    if (!payment.supplierId?.trim()) {
        errors.push({ field: 'supplierId', message: 'Supplier is required' });
    }
    if (!payment.paymentFor) {
        errors.push({ field: 'paymentFor', message: 'Payment purpose is required' });
    }
    if (!payment.paymentDate) {
        errors.push({ field: 'paymentDate', message: 'Payment date is required' });
    }
    if (payment.amount === undefined || payment.amount <= 0) {
        errors.push({ field: 'amount', message: 'Amount must be greater than 0' });
    }
    if (!payment.paymentMethod) {
        errors.push({ field: 'paymentMethod', message: 'Payment method is required' });
    }
    return {
        isValid: errors.length === 0,
        errors
    };
}
/**
 * Generate customer payment number
 * Format: CPAY-YYYY-NNN
 * Example: CPAY-2025-001
 */
export function generateCustomerPaymentNumber(year, sequence) {
    return `CPAY-${year}-${sequence.toString().padStart(3, '0')}`;
}
/**
 * Generate supplier payment number
 * Format: SPAY-YYYY-NNN
 * Example: SPAY-2025-001
 */
export function generateSupplierPaymentNumber(year, sequence) {
    return `SPAY-${year}-${sequence.toString().padStart(3, '0')}`;
}
/**
 * Format customer payment for display
 */
export function formatCustomerPaymentDisplay(payment) {
    return `${payment.paymentNumber} - ${payment.customerName} ($${payment.amount.toLocaleString()})`;
}
/**
 * Format supplier payment for display
 */
export function formatSupplierPaymentDisplay(payment) {
    return `${payment.paymentNumber} - ${payment.supplierName} ($${payment.amount.toLocaleString()})`;
}
/**
 * Check if customer payment is actionable (pending or bounced)
 */
export function isCustomerPaymentActionable(payment) {
    return payment.status === 'pending' || payment.status === 'bounced';
}
/**
 * Check if supplier payment is actionable (pending)
 */
export function isSupplierPaymentActionable(payment) {
    return payment.status === 'pending';
}
/**
 * Get days since payment (for aging reports)
 */
export function getDaysSincePayment(paymentDate) {
    const payment = new Date(paymentDate);
    const now = new Date();
    const diffTime = now.getTime() - payment.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
}
